/* eslint-disable jsx-a11y/aria-props */
/*
 * WebCRD
 * Web to print solution that automates ordering, fulfillment, job ticketing, production management and chargebacks across corporate print centers.
 * Copyright 1999-2026 Rochester Software Associates (service@rocsoft.com)
 */


/*
 * WebCRD
 * Web to print solution that automates ordering, fulfillment, job ticketing, production management and chargebacks across corporate print centers.
 * Copyright 1999-2026 Rochester Software Associates (service@rocsoft.com)
 */

import PropTypes from 'prop-types';
import { Fragment, memo, useCallback, useEffect, useState } from 'react';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';

import useOnOffToggle from '~/utils/hooks/useOnOffToggle';
import FeatherIcon from '~components/images/icons/FeatherIcon';
import localizedStringOrNode, { localizedStringOrNodePropTypes } from '~components/text/LocalizedStringOrNode';
import useLocalizationTextFormatted from '~components/text/useLocalizationTextFormatted';
import { getLogger } from '~utils/logging';

import Button from '../buttons/Button';
import { automationIDPropTypes, getExtraAutomationID, useMemoizedAutomationID } from '../id/AutomationID';
import IDList from '../id/IDList';
import MappingID from '../id/MappingID';
import { rootAutomationIDPropTypes } from '../id/RootAutomationID';
import TextInput from '../inputs/TextInput';

import AdvancedSearchModal from './AdvancedSearchModal';

const logger = getLogger(Symbol('Components:Page:Header:HeaderSearch'));

/**
 * Takes a list of search categories and searches it for the default category key
 *
 * @param {Array<object>} categories - A list of categories used for the search
 * @param {string|number} defaultCategoryKey - The key for the default category for this list
 * @returns {object} Returns the default category from the list
 */
const getDefaultCategory = (categories, defaultCategoryKey) => {
    if (!categories || categories.length === 0) {
        return null;
    }
    let defaultCategory = categories.find((category) => category.key === defaultCategoryKey);
    if (!defaultCategory) {
        defaultCategory = categories[0];
    }
    return defaultCategory;
};

/**
 * Makes a specialized drop down menu of the categories supplied that pairs with search
 *
 * @param {object} props
 * @param {object} props.automationID - automation ID for the section this is from
 * @param {Array<object>} props.categories - the categories used to populate the dropdown
 * @param {object|string} props.selectedCategory - the current selected category
 * @param {PropTypes.func} props.setSelectedCategory - the function that updates the selected category
 * @returns {PropTypes.node} Returns a dropdown component withe the categories listed and the selected category selected
 */
const CategoriesMenu = ({ automationID, categories, selectedCategory, setSelectedCategory }) => {
    const ariaDescription = useLocalizationTextFormatted('ORDERING_UI.ARIA.HEADER__CATALOG_SEARCH_FILTERS');

    if (!categories || categories.length === 0) {
        return null;
    }
    const buttonAutomationID = getExtraAutomationID(automationID, 'button');

    const ids = [];
    return (
        <UncontrolledDropdown className="btn-group categories-btn">
            <DropdownToggle id={buttonAutomationID.id} aria-description={ariaDescription} caret>{selectedCategory && localizedStringOrNode(selectedCategory.label)}</DropdownToggle>
            <DropdownMenu>
                {categories.map((category) => {
                    const optionAutomationID = getExtraAutomationID(automationID, 'option', category.key);
                    ids.push(category.key);
                    return (
                        <Fragment key={category.key}>
                            <DropdownItem id={optionAutomationID.id} key={category.key} onClick={() => setSelectedCategory(category)}>
                                {localizedStringOrNode(category.label)}
                            </DropdownItem>
                            {!isNaN(category.key) && <MappingID label="CategoryIDs" name={category.label} id={category.key} />}
                        </Fragment>
                    );
                })}
                <IDList label="CategoryIDs" ids={ids} />
            </DropdownMenu>
        </UncontrolledDropdown>
    );
};

CategoriesMenu.propTypes = {
    automationID: automationIDPropTypes,
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: localizedStringOrNodePropTypes,
        })
    ),
    selectedCategory: PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: localizedStringOrNodePropTypes,
    }),
    setSelectedCategory: PropTypes.func,
};

const MemoizedCategoriesMenu = memo(CategoriesMenu);


/**
 * Creates and returns the search for the ordering role header bar
 *
 * @param {object} props
 * @param {object} props.rootID - automation ID for the root section this is from
 * @param {boolean} props.hiddenOnMobile - flag that determines whether this should be hidden on mobile
 * @param {Array<object>} props.categories - A list of categories used for the search
 * @param {string|number} props.defaultCategoryKey - The key for the default category in the categories list
 * @param {Function} props.onSearch - A function to run the search
 * @param {string} props.placeholderTextKey - Placeholder text for the searchbar if nothing is in there
 * @param {string} props.searchAltTextKey - Alt text for the search button
 * @param {string} props.advancedSearchAltTextKey - Alt text for the advanced search button
 * @param {string} props.resetSearchAltTextKey - Alt text for the reset search button
 * @param {string} props.currentSearch - What is currently contained in the search
 * @param {string|number} props.currentCategoryKey - The key for the currently selected category in the categories list
 * @returns {PropTypes.node} Returns the search bar
 */
const HeaderSearch = ({
    rootID,
    hiddenOnMobile,
    categories,
    defaultCategoryKey,
    onSearch,
    placeholderTextKey,
    searchAltTextKey,
    advancedSearchAltTextKey,
    resetSearchAltTextKey,
    currentSearch,
    currentCategoryKey,
}) => {
    const defaultCategory = getDefaultCategory(categories, defaultCategoryKey);
    const [selectedCategory, setSelectedCategory] = useState(defaultCategory);

    useEffect(() => {
        if (currentCategoryKey) {
            const currentCategory = categories.find((category) => category.key === currentCategoryKey);
            if (currentCategory) {
                setSelectedCategory(currentCategory);
            }
        }
    }, [currentCategoryKey, categories]);

    // Saved search is currently set in the ordering-specific useBasicSearchOptions.js
    // All other roles simply ignore this and fallback to default
    const [search, setSearch] = useState(currentSearch);

    useEffect(() => {
        setSearch(currentSearch);
    }, [currentSearch]);

    const onInput = useCallback(
        (event) => {
            if (event) {
                setSearch(event.target.value);
            }
        },
        [setSearch]
    );

    const searchPlaceholder = useLocalizationTextFormatted(placeholderTextKey);
    const searchAlt = useLocalizationTextFormatted(searchAltTextKey);
    const advancedSearchAlt = useLocalizationTextFormatted(advancedSearchAltTextKey);
    const resetAlt = useLocalizationTextFormatted(resetSearchAltTextKey);

    const onSubmit = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (logger.isDebugEnabled()) {
                logger.debug(`HeaderSearch submitted`, { selectedCategory, search });
            }
            onSearch({ categoryKey: selectedCategory.key, search });
        /* eslint-disable-next-line rocsoft-rules/granular-deps */
        }, [selectedCategory, search, onSearch]
    );

    const onReset = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            setSearch('');
            setSelectedCategory(defaultCategory);
        },
        [setSearch, setSelectedCategory, defaultCategory]
    );

    const wrapClasses = ['search-box-wrap'];
    if (hiddenOnMobile) {
        wrapClasses.push('hidden-on-mobile');
    }

    const ariaLabel = useLocalizationTextFormatted('ORDERING_UI.ARIA.HEADER__ITEM_SEARCH');
    const searchFieldAutomationID = useMemoizedAutomationID(rootID, 'searchInput');
    const searchCatalogDropdownAutomationID = useMemoizedAutomationID(rootID, 'searchCatalogDropdown');
    const searchAutomationID = useMemoizedAutomationID(rootID, 'searchButton');
    const advancedSearchAutomationID = useMemoizedAutomationID(rootID, 'advancedSearchButton');
    const advancedSearchModalAutomationID = useMemoizedAutomationID(rootID, 'advancedSearch');
    const resetSearchAutomationID = useMemoizedAutomationID(rootID, 'resetSearchButton');

    const [showAdvancedSearchModal, openAdvancedSearchModal, /* closeModal */, toggleAdvancedSearchModal] = useOnOffToggle(false);

    const showResetButton = (search || selectedCategory.key !== defaultCategory.key)
        && <Button action={onReset} buttonType="reset" type="linkDanger" tooltip={resetAlt} className="resetButton" automationID={resetSearchAutomationID}>
            <FeatherIcon name="x" />
        </Button>;

    return (
        <div className={wrapClasses.join(' ')}>
            <div className="search-box-inner">
                <form className="search-box" onSubmitCapture={onSubmit} onResetCapture={onReset}>
                    <MemoizedCategoriesMenu
                        automationID={searchCatalogDropdownAutomationID}
                        categories={categories}
                        selectedCategory={selectedCategory}
                        setSelectedCategory={setSelectedCategory}
                    />
                    <div className="input-group">
                        <TextInput
                            ariaLabel={ariaLabel}
                            automationID={searchFieldAutomationID}
                            placeholder={searchPlaceholder}
                            value={search}
                            setValue={setSearch}
                            onEnter={onInput}
                        />
                        <span className="input-group-btn">
                            {showResetButton}
                            <Button action={onSubmit} buttonType="submit" type="linkSecondary" tooltip={searchAlt} automationID={searchAutomationID}>
                                <FeatherIcon name="search" />
                            </Button>
                            <Button type="linkSecondary" action={openAdvancedSearchModal} tooltip={advancedSearchAlt} automationID={advancedSearchAutomationID}>
                                <FeatherIcon name="sliders" />
                            </Button>
                        </span>
                    </div>
                </form>
                {showAdvancedSearchModal && <AdvancedSearchModal
                    automationID={advancedSearchModalAutomationID}
                    toggleModal={toggleAdvancedSearchModal}
                    onSearch={onSearch}
                    initialSearchValue={search}
                    categories={categories}
                    initialCategoryValue={selectedCategory}
                />}
            </div>
        </div>
    );
};

HeaderSearch.propTypes = {
    rootID: rootAutomationIDPropTypes,
    hiddenOnMobile: PropTypes.bool,
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: localizedStringOrNodePropTypes,
        })
    ),
    defaultCategoryKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSearch: PropTypes.func.isRequired,
    placeholderTextKey: PropTypes.string.isRequired,
    searchAltTextKey: PropTypes.string.isRequired,
    advancedSearchAltTextKey: PropTypes.string.isRequired,
    resetSearchAltTextKey: PropTypes.string.isRequired,
    currentSearch: PropTypes.string,
    currentCategoryKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // normalized catalog ID
};

export default HeaderSearch;
